import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ImageIcon, X, Crown, AlertCircle } from "lucide-react";
import SecureImage from "@/components/SecureImage";
import useSubscription from "@/contexts/SubscriptionContext";
import { canUploadImages } from "@/lib/featureGates";
import { useTranslation } from "react-i18next";

const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic'];
const MAX_FILE_SIZE = 4 * 1024 * 1024; // 4MB

interface EnhancedImageUploaderProps {
  imageFile: File | null;
  imagePreview: string | null;
  imageUrl?: string;
  onImageChange: (file: File) => void;
  onImageRemove?: () => void;
  required?: boolean;
  disabled?: boolean;
}

const EnhancedImageUploader: React.FC<EnhancedImageUploaderProps> = ({
  imageFile,
  imagePreview,
  imageUrl,
  onImageChange,
  onImageRemove,
  required = false,
  disabled = false
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { status: subscriptionStatus } = useSubscription();
  const tierBlocked = !canUploadImages(subscriptionStatus);
  const effectivelyDisabled = disabled || tierBlocked;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileError(null);

    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      setFileError('Desteklenmeyen dosya formatı. Lütfen JPEG, PNG, GIF veya WEBP seçin.');
      e.target.value = '';
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setFileError('Dosya çok büyük. Maksimum 4MB.');
      e.target.value = '';
      return;
    }

    onImageChange(file);
    e.target.value = '';
  };

  const handleRemoveImage = () => {
    setFileError(null);
    if (onImageRemove) {
      onImageRemove();
    } else {
      const emptyFile = new File([], '', { type: 'image/jpeg' });
      onImageChange(emptyFile);
    }
  };

  const displayImage = imagePreview || imageUrl;

  return (
    <div className="space-y-2">
      <label className="block text-[11px] font-semibold uppercase tracking-[0.04em] text-gray-600">
        Fotoğraf
      </label>

      {displayImage && (
        <div className="relative w-full aspect-video rounded-xl overflow-hidden border border-gray-200 bg-gray-50 mb-2">
          <SecureImage
            src={displayImage}
            alt="Giriş fotoğrafı"
            className="w-full h-full object-cover"
          />
          {!disabled && (
            <Button
              type="button"
              variant="destructive"
              size="icon"
              className="absolute top-2 right-2 h-7 w-7 rounded-full shadow-sm"
              onClick={handleRemoveImage}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      )}

      {tierBlocked ? (
        <Button
          type="button"
          variant="outline"
          onClick={() => navigate('/subscription')}
          className="w-full flex items-center justify-center py-5 border-2 border-dashed border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100 rounded-xl"
        >
          <Crown className="h-4 w-4 mr-2" />
          <span className="text-[13px] font-medium">{t('photo_upload.premium_required', 'Fotoğraf yüklemek için Premium gerekli')}</span>
        </Button>
      ) : (
        <>
          <Button
            type="button"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={effectivelyDisabled}
            className="w-full flex items-center justify-center py-5 border-2 border-dashed border-gray-200 bg-white text-gray-500 hover:border-brand-300 hover:text-brand-600 hover:bg-brand-25 rounded-xl"
          >
            <ImageIcon className="h-4 w-4 mr-2" />
            <span className="text-[13px] font-medium">{displayImage ? 'Fotoğrafı Değiştir' : 'Fotoğraf Ekle'}</span>
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp,image/heic"
            onChange={handleFileSelect}
            className="hidden"
            disabled={effectivelyDisabled}
          />
        </>
      )}

      {fileError && (
        <div className="flex items-center text-destructive text-sm">
          <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
          <span>{fileError}</span>
        </div>
      )}

      {required && (
        <input
          type="hidden"
          required={required && !displayImage}
        />
      )}
    </div>
  );
};

export default EnhancedImageUploader;
